import { z } from 'zod'
import { billingActorKindSchema } from '../enums/actor-kind.enum.js'
import { billingPurchaseSourceSchema } from '../enums/billing-purchase-source.enum.js'
import {
  billingPurchaseStatusSchema,
  CLOSED_PURCHASE_STATUSES,
  SETTLED_CHECKOUT_STATUSES,
  UNPAID_PURCHASE_STATUSES,
} from '../enums/billing-purchase-status.enum.js'
import { jsonAmountSchema, nonnegativeJsonAmountSchema, positiveJsonAmountSchema } from '../helpers/money.js'
import { uuidSchema } from '../validators/params.js'

/*
 * Amounts: inputs are whole cents; balances, ledger amounts and prices come out
 * in micro-dollars (1,000,000 = $1.00), so a sub-cent price needs no rounding.
 */

/** A saved card. Brand stays an open string: Stripe adds brands, and an unknown one must not fail the whole summary. */
export const billingCardDtoSchema = z.object({
  brand: z.string(),
  last4: z.string().regex(/^\d{4}$/),
  expMonth: z.number().int().min(1).max(12),
  expYear: z.number().int(),
})

export type BillingCardDto = z.infer<typeof billingCardDtoSchema>

const autoReloadCount = {
  /** Reloads (pending or done) this calendar month, in the organization's timezone. */
  reloadedThisMonthCents: nonnegativeJsonAmountSchema,
}

const autoReloadNumbers = {
  triggerMicros: jsonAmountSchema,
  amountCents: jsonAmountSchema,
  monthlyCapCents: jsonAmountSchema,
}

/**
 * Off is either never configured (no numbers) or turned off while keeping the
 * numbers it last used. Active and paused always have those numbers. A paused
 * reason is only meaningful once it has paused; active clears it.
 */
export const billingAutoReloadDtoSchema = z.union([
  z.object({
    status: z.literal('off'),
    triggerMicros: z.null(),
    amountCents: z.null(),
    monthlyCapCents: z.null(),
    /** Left over when auto-reload was paused, then turned off. */
    pausedReason: z.string().nullable(),
    ...autoReloadCount,
  }),
  z.object({
    status: z.literal('off'),
    ...autoReloadNumbers,
    pausedReason: z.string().nullable(),
    ...autoReloadCount,
  }),
  z.object({
    status: z.literal('active'),
    ...autoReloadNumbers,
    pausedReason: z.null(),
    ...autoReloadCount,
  }),
  z.object({
    status: z.literal('paused'),
    ...autoReloadNumbers,
    /** The card's failure, as Stripe reported it. Null when Stripe sent none. */
    pausedReason: z.string().nullable(),
    ...autoReloadCount,
  }),
])

export type BillingAutoReloadDto = z.infer<typeof billingAutoReloadDtoSchema>

export const billingSummaryDtoSchema = z.object({
  organizationId: uuidSchema,
  /** PixelSign doesn't bill this organization: its live sends are recorded at $0.00. */
  exempt: z.boolean(),
  balanceMicros: nonnegativeJsonAmountSchema,
  grantedMicros: nonnegativeJsonAmountSchema,
  paidMicros: nonnegativeJsonAmountSchema,
  /** Paid credit not yet spent, in whole cents (as micros): what a refund can return before its fee. */
  refundableMicros: nonnegativeJsonAmountSchema,
  /** The price of one live envelope now. */
  priceMicros: positiveJsonAmountSchema,
  nextPriceChange: z
    .object({ priceMicros: positiveJsonAmountSchema, effectiveAt: z.string().datetime() })
    .nullable(),
  lowBalanceThresholdMicros: nonnegativeJsonAmountSchema,
  autoReload: billingAutoReloadDtoSchema,
  card: billingCardDtoSchema.nullable(),
})

export type BillingSummaryDto = z.infer<typeof billingSummaryDtoSchema>

export const billingActorDtoSchema = z.object({
  kind: billingActorKindSchema,
  /** The key's or person's name. An organization sees staff as "PixelSign staff". */
  name: z.string(),
})

export type BillingActorDto = z.infer<typeof billingActorDtoSchema>

/**
 * Where a refund of credit stands: `succeeded` once every card refund did, `needs_attention` when any failed, else `pending`.
 * The rollup over the whole refund, which the dashboard shows; each card refund part keeps its own `BillingRefundStatus`.
 */
export const billingRefundOutcomeSchema = z.enum(['pending', 'succeeded', 'needs_attention'])

export type BillingRefundOutcome = z.infer<typeof billingRefundOutcomeSchema>

const entryShared = {
  id: uuidSchema,
  /** Signed: credit coming in is positive, a charge or refund negative. */
  amountMicros: jsonAmountSchema,
  balanceAfterMicros: nonnegativeJsonAmountSchema,
  createdAt: z.string().datetime(),
  actor: billingActorDtoSchema,
  /** The AI app it came through, if any. */
  app: z.string().nullable(),
}

const entryCardSchema = z.object({
  brand: z.string(),
  last4: z.string().regex(/^\d{4}$/),
})

const entryPurchaseShared = {
  id: uuidSchema,
  source: billingPurchaseSourceSchema,
  creditCents: jsonAmountSchema,
  card: entryCardSchema.nullable(),
}

/** Tax is known once the purchase succeeded. Earlier and failed purchases have none. */
const entryPurchaseSchema = z.discriminatedUnion('status', [
  z.object({
    ...entryPurchaseShared,
    status: z.literal('succeeded'),
    taxCents: jsonAmountSchema.nullable(),
    totalCents: jsonAmountSchema.nullable(),
  }),
  z.object({
    ...entryPurchaseShared,
    status: z.enum(UNPAID_PURCHASE_STATUSES),
    taxCents: z.null(),
    totalCents: z.null(),
  }),
])

const entryRefundSchema = z.object({
  id: uuidSchema,
  feeCents: jsonAmountSchema,
  netCents: jsonAmountSchema,
  taxCents: jsonAmountSchema,
  status: billingRefundOutcomeSchema,
})

const entryNoLinks = {
  envelopeId: z.null(),
  unitPriceMicros: z.null(),
  exempt: z.literal(false),
  purchase: z.null(),
  refund: z.null(),
  grantReason: z.null(),
}

function creditEntry<Type extends 'top_up' | 'auto_reload'>(type: Type) {
  return z.object({
    ...entryShared,
    type: z.literal(type),
    ...entryNoLinks,
    /** Absent when a repair or a test recorded the credit without a payment. */
    purchase: entryPurchaseSchema.nullable(),
  })
}

function refundEntry<Type extends 'refund' | 'closure_refund'>(type: Type) {
  return z.object({
    ...entryShared,
    type: z.literal(type),
    ...entryNoLinks,
    refund: entryRefundSchema,
  })
}

/**
 * Each entry type carries only the links that type is written with. A send
 * charge names its envelope and the price it was made at. A refund names its
 * refund. The other links are null.
 */
export const billingEntryDtoSchema = z.discriminatedUnion('type', [
  z.object({
    ...entryShared,
    type: z.literal('send_charge'),
    envelopeId: uuidSchema,
    unitPriceMicros: positiveJsonAmountSchema,
    /** A send recorded at $0.00 because the organization isn't billed. */
    exempt: z.boolean(),
    purchase: z.null(),
    refund: z.null(),
    grantReason: z.null(),
  }),
  creditEntry('top_up'),
  creditEntry('auto_reload'),
  z.object({
    ...entryShared,
    type: z.literal('grant'),
    ...entryNoLinks,
    /** Why PixelSign granted this credit. Null when the grant recorded none. */
    grantReason: z.string().nullable(),
  }),
  refundEntry('refund'),
  refundEntry('closure_refund'),
  z.object({
    ...entryShared,
    type: z.literal('closure_forfeit'),
    ...entryNoLinks,
  }),
])

export type BillingEntryDto = z.infer<typeof billingEntryDtoSchema>

export const billingHistoryDtoSchema = z.object({
  entries: z.array(billingEntryDtoSchema),
  /** Pass as `before` for the next, older page; null on the last page. */
  nextBefore: z.string().nullable(),
})

export type BillingHistoryDto = z.infer<typeof billingHistoryDtoSchema>

const purchaseShared = {
  id: uuidSchema,
  source: billingPurchaseSourceSchema,
  /** The credit bought, before tax. */
  creditCents: jsonAmountSchema,
  createdAt: z.string().datetime(),
  /** The organization's balance now. */
  balanceMicros: nonnegativeJsonAmountSchema,
}

/**
 * A credit purchase. The Checkout link exists only while it can still be paid
 * (and may still be missing if Stripe has not opened a page). Tax is recorded
 * when it succeeds. A pending purchase can say why credit was not added when
 * Stripe collected a different amount. A failure message is recorded when the
 * purchase closes without succeeding.
 */
export const billingPurchaseDtoSchema = z.discriminatedUnion('status', [
  z.object({
    ...purchaseShared,
    status: z.literal('pending'),
    taxCents: z.null(),
    totalCents: z.null(),
    checkoutUrl: z.string().url().nullable(),
    failureMessage: z.string().nullable(),
    succeededAt: z.null(),
  }),
  z.object({
    ...purchaseShared,
    status: z.literal('succeeded'),
    /** Sales tax, once paid; null where none applies. */
    taxCents: jsonAmountSchema.nullable(),
    totalCents: jsonAmountSchema.nullable(),
    checkoutUrl: z.null(),
    failureMessage: z.null(),
    succeededAt: z.string().datetime().nullable(),
  }),
  z.object({
    ...purchaseShared,
    status: z.enum(CLOSED_PURCHASE_STATUSES),
    taxCents: z.null(),
    totalCents: z.null(),
    checkoutUrl: z.null(),
    failureMessage: z.string().nullable(),
    succeededAt: z.null(),
  }),
])

export type BillingPurchaseDto = z.infer<typeof billingPurchaseDtoSchema>

/**
 * The same purchase as one object, for callers whose schema must be an object
 * (an MCP tool output). Parsing still rejects a combination the union rejects.
 */
export const billingPurchaseObjectSchema = z
  .object({
    id: uuidSchema,
    source: billingPurchaseSourceSchema,
    status: billingPurchaseStatusSchema,
    creditCents: jsonAmountSchema,
    taxCents: jsonAmountSchema.nullable(),
    totalCents: jsonAmountSchema.nullable(),
    checkoutUrl: z.string().url().nullable(),
    failureMessage: z.string().nullable(),
    createdAt: z.string().datetime(),
    succeededAt: z.string().datetime().nullable(),
    balanceMicros: nonnegativeJsonAmountSchema,
  })
  .superRefine((value, ctx) => {
    if (billingPurchaseDtoSchema.safeParse(value).success) return
    ctx.addIssue({ code: 'custom', message: 'Purchase fields do not match its status' })
  })

/** A refund of unused paid credit, less the fee, back to the card payments it came from. */
export const billingRefundDtoSchema = z.object({
  id: uuidSchema,
  /** Credit taken off the balance. */
  amountMicros: positiveJsonAmountSchema,
  feeCents: jsonAmountSchema,
  /** What goes back to the cards, before the sales tax that's returned with it. */
  netCents: jsonAmountSchema,
  /** Sales tax returned with the net. */
  taxCents: jsonAmountSchema,
  status: billingRefundOutcomeSchema,
  createdAt: z.string().datetime(),
  /** The organization's balance after the refund. */
  balanceMicros: nonnegativeJsonAmountSchema,
})

export type BillingRefundDto = z.infer<typeof billingRefundDtoSchema>

/** Auto-reload's numbers: when the balance falls below the trigger, add the amount (plus tax), up to the cap each calendar month. */
export const autoReloadSettingsDtoSchema = z.object({
  triggerCents: jsonAmountSchema,
  amountCents: jsonAmountSchema,
  monthlyCapCents: jsonAmountSchema,
})

export type AutoReloadSettingsDto = z.infer<typeof autoReloadSettingsDtoSchema>

const cardSetupShared = {
  id: uuidSchema,
  /** Settings applied, and auto-reload turned on, once the card is saved. Kept after it settles so the return page can say what turned on. */
  pendingAutoReload: autoReloadSettingsDtoSchema.nullable(),
}

/** Saving a card for auto-reload through a Stripe Checkout page (setup mode: nothing is charged). */
export const billingCardSetupDtoSchema = z.discriminatedUnion('status', [
  z.object({
    ...cardSetupShared,
    status: z.literal('pending'),
    /** Stripe's page while the setup is pending. Null if Stripe has not opened one yet. */
    checkoutUrl: z.string().url().nullable(),
  }),
  z.object({
    ...cardSetupShared,
    status: z.enum(SETTLED_CHECKOUT_STATUSES),
    checkoutUrl: z.null(),
  }),
])

export type BillingCardSetupDto = z.infer<typeof billingCardSetupDtoSchema>

/** One price per live envelope in the schedule, as staff see it. */
export const billingPriceDtoSchema = z.object({
  id: uuidSchema,
  priceMicros: positiveJsonAmountSchema,
  effectiveAt: z.string().datetime(),
  /** Whether it's more than the price before it; null for the first price. */
  isIncrease: z.boolean().nullable(),
  /** The staff member who scheduled it; null for the launch price. */
  scheduledBy: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type BillingPriceDto = z.infer<typeof billingPriceDtoSchema>

/** The price schedule: the price now, changes still to come (soonest first), and the latest past ones. */
export const billingPriceScheduleDtoSchema = z.object({
  current: billingPriceDtoSchema,
  scheduled: z.array(billingPriceDtoSchema),
  past: z.array(billingPriceDtoSchema),
})

export type BillingPriceScheduleDto = z.infer<typeof billingPriceScheduleDtoSchema>

/** What staff see: the summary plus the Stripe customer and refunds Stripe couldn't complete. */
export const opsBillingDtoSchema = billingSummaryDtoSchema.extend({
  stripeCustomerId: z.string().nullable(),
  refundsNeedingAttention: z.array(
    z.object({
      refundId: uuidSchema,
      /** The Stripe refund that failed; null for net a closing refund couldn't place on any payment. */
      partId: uuidSchema.nullable(),
      purchaseId: uuidSchema.nullable(),
      stripePaymentIntentId: z.string().nullable(),
      refundCents: jsonAmountSchema,
      failureMessage: z.string().nullable(),
      unallocatedCents: jsonAmountSchema,
      createdAt: z.string().datetime(),
    }),
  ),
})

export type OpsBillingDto = z.infer<typeof opsBillingDtoSchema>
